import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { externalUrlGroupsTranslation } from '../constants';
import { ExternalUrlGroupsState } from '../types-store';
import { ExternalUrlGroupsFormSections } from './components/external-url-form-sections';
import { actionCreators } from './actions';
import { ExternalUrlFormSelectorProps, selectors } from './selectors';
import { ExternalUrlGroupItem } from './types-store';
import { getExternalUrlAdminAccess } from 'app/area/external-url-groups/get-access';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { ExternalUrlGroupsActionType } from 'app/area/external-url-groups/form/types';

type ExternalUrlFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & ExternalUrlGroupsState
  & ExternalUrlFormSelectorProps
  & typeof actionCreators;

interface ExternalUrlFormState {
  showTranslations: boolean;
}

class ExternalUrlGroupsFormComponent extends React.PureComponent<ExternalUrlFormProps, ExternalUrlFormState> {
  public static statePath: string = STATE.externalUrlGroups;

  public state: Readonly<ExternalUrlFormState> = {
    showTranslations: false
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: ExternalUrlFormProps) {
    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }

    if (prevProps?.form?.validation?.language !== this.props?.form?.validation?.language) {
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState, createExternalUrl, loadExternalUrlGroup, displayError, translation } = this.props;

    const externalUrlAccess = getExternalUrlAdminAccess(sharedState);
    if (!externalUrlAccess) {
      displayError(translation.no_access_to_external_urls);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadExternalUrlGroup(id);
    } else {
      createExternalUrl();
    }
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private updateTranslationsToggle = () => {
    if (!!this.props?.form?.validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private saveExternalUrlGroup = (externalUrlGroupItem: ExternalUrlGroupItem) => {
    const {
      params: { actionType } = {}, saveExternalUrlGroup, translation } = this.props;

    const actionTypeId = ExternalUrlGroupsActionType[actionType];
    const successMessage = (isDefinedIncludingZero(externalUrlGroupItem?.id)
      && actionTypeId === ExternalUrlGroupsActionType.edit)
      ? translation.external_url_updated
      : translation.external_url_created;

    saveExternalUrlGroup(actionTypeId, externalUrlGroupItem, successMessage, translation.external_url_validation_error);
  };

  private deleteExternalUrlGroup = () => {
    const { id, form: { externalUrlGroupItem: { language: externalUrlLanguage = [] } = {} } = {},
      sharedState: { language: { value: languageValue } = {} } = {},
      translation, deleteExternalUrlGroup } = this.props;

    deleteExternalUrlGroup(
      id,
      externalUrlLanguage.find(option => option.value === languageValue)?.description,
      translation.external_url_deleted
    );
  };

  public render() {
    const { section, sharedState, form, translation,
      options, shouldPrompt,
      params: { actionType },
      loadExternalUrlOptions,
      loadOptions, changeLanguage, copyLanguageToAll, changeLongDescription, copyLongDescriptionToAll,
      updateItem, updateAccessPolicyItem, removeAccessPolicy, addAccessPolicy,
      resetAccessPolicyClientValidation, resetAccessPolicyServerValidation, removeAllAccessPolicy,
      updateExternalUrl, addExternalUrls, deleteExternalUrl, deleteAllExternalUrls,
      updateValidation, displayError
    } = this.props;
    const mainLanguage = sharedState?.language?.value ?? undefined;
    const locale = sharedState?.locale || '';
    const translationAdminAccess = sharedState?.access?.adminPanel?.translation?.administrationCentral;
    const externalUrlAccess = getExternalUrlAdminAccess(sharedState);

    return <div className="entity-view">
      <Paper componentId="external-url-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}

        {!form.languageOptions || !form.externalUrlGroupItem || form.fetching
          ? <SpinLoader />
          : <ExternalUrlGroupsFormSections
            actionType={ExternalUrlGroupsActionType[actionType]}
            formState={form}
            shouldPrompt={shouldPrompt}
            mainLanguage={mainLanguage}
            saveExternalUrlGroup={this.saveExternalUrlGroup}
            deleteExternalUrlGroup={this.deleteExternalUrlGroup}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}
            changeLongDescription={changeLongDescription}
            copyLongDescriptionToAll={copyLongDescriptionToAll}
            options={options}
            loadOptions={loadOptions}
            externalUrlOptions={this.props.form.externalUrlOptions}
            loadExternalUrlOptions={loadExternalUrlOptions}
            updateValidation={updateValidation}
            displayError={displayError}
            section={section}
            locale={locale}
            translation={translation}
            translationAdminAccess={translationAdminAccess}
            externalUrlAccess={externalUrlAccess}
            showTranslations={this.state.showTranslations}
            switchTranslationsToggle={this.switchTranslationsToggle}
            updateAccessPolicyItem={updateAccessPolicyItem}
            removeAccessPolicy={removeAccessPolicy}
            addAccessPolicy={addAccessPolicy}
            resetAccessPolicyClientValidation={resetAccessPolicyClientValidation}
            resetAccessPolicyServerValidation={resetAccessPolicyServerValidation}
            removeAllAccessPolicy={removeAllAccessPolicy}
            addExternalUrls={addExternalUrls}
            updateExternalUrl={updateExternalUrl}
            deleteExternalUrl={deleteExternalUrl}
            deleteAllExternalUrls={deleteAllExternalUrls}
            accessPolicyValidation={undefined}
            externalUrlsValidation={undefined} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(ExternalUrlGroupsFormComponent)),
  actionCreators, selectors, externalUrlGroupsTranslation
);

export { container as ExternalUrlGroupsForm };
